import React from "react";
import { AlertTriangle, WifiOff, X } from "lucide-react";
import type { LanStatusDetail } from "@/hooks/usePeerConnectionManager";

interface MobileNetworkWarningProps {
  open: boolean;
  onClose?: () => void;
  reason?: "mismatch" | "network-error";
  networkDetails?: LanStatusDetail;
}

const messages = {
  mismatch: {
    title: "Devices are on different networks",
    body: "Move your laptop and phone onto the same Wi-Fi network, then try again.",
  },
  "network-error": {
    title: "We lost the LAN connection",
    body: "Reconnect both devices to the same Wi-Fi network before retrying.",
  },
};

const MobileNetworkWarning: React.FC<MobileNetworkWarningProps> = ({
  open,
  onClose,
  reason = "network-error",
  networkDetails,
}) => {
  if (!open) {
    return null;
  }

  const copyDetails = [
    { label: "Laptop", value: networkDetails?.laptop?.normalizedSignature ?? "Unknown" },
    { label: "Mobile", value: networkDetails?.mobile?.normalizedSignature ?? "Unknown" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-semibold text-slate-900">{messages[reason].title}</p>
              <p className="text-xs text-slate-500">{messages[reason].body}</p>
            </div>
          </div>
          <button
            type="button"
            className="rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            onClick={onClose}
            aria-label="Close network warning"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-2 text-xs font-semibold uppercase text-slate-500">
            <WifiOff className="h-4 w-4" />
            Connection details
          </div>
          <div className="divide-y divide-slate-200">
            {copyDetails.map((detail) => (
              <div key={detail.label} className="flex items-center justify-between px-4 py-2 text-sm text-slate-600">
                <span className="font-medium text-slate-500">{detail.label}</span>
                <span className="font-mono text-xs text-slate-900">{detail.value}</span>
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="mt-6 w-full rounded-lg bg-slate-900 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
        >
          Got it, I'll retry
        </button>
      </div>
    </div>
  );
};

export default MobileNetworkWarning;
